import type { CreateProjectBody } from "~/api/model/createProjectBody";
import type { Document } from "~/api/model/document";
import type { Folder } from "~/api/model/folder";
import type { GetOrganizationSettings200 } from "~/api/model/getOrganizationSettings200";
import type { UpdateOrganizationSettingsBody } from "~/api/model/updateOrganizationSettingsBody";

export const BATES_DEFAULT_START_NUMBER = 1;
export const BATES_DEFAULT_DIGITS = 6;
export const BATES_MIN_DIGITS = 3;
export const BATES_MAX_DIGITS = 10;
export const BATES_AFFIX_MAX_LENGTH = 32;
export const BATES_SEPARATOR = "-";

export const BATES_PREFIX_SOURCES = [
  "CASE_NAME",
  "FILE_NAME",
  "CUSTOM",
] as const;

export type BatesPrefixSource = (typeof BATES_PREFIX_SOURCES)[number];

export const BATES_DEFAULT_PREFIX_SOURCE: BatesPrefixSource = "CASE_NAME";

export const isBatesPrefixSource = (
  value: unknown
): value is BatesPrefixSource =>
  typeof value === "string" &&
  (BATES_PREFIX_SOURCES as readonly string[]).includes(value);

export const BATES_SUFFIX_SOURCES = ["NONE", "FILE_NAME", "CUSTOM"] as const;

export type BatesSuffixSource = (typeof BATES_SUFFIX_SOURCES)[number];

export const BATES_DEFAULT_SUFFIX_SOURCE: BatesSuffixSource = "NONE";

export const isBatesSuffixSource = (
  value: unknown
): value is BatesSuffixSource =>
  typeof value === "string" &&
  (BATES_SUFFIX_SOURCES as readonly string[]).includes(value);

export const BATES_STAMP_POSITIONS = [
  "TOP_LEFT",
  "TOP_CENTER",
  "TOP_RIGHT",
  "BOTTOM_LEFT",
  "BOTTOM_CENTER",
  "BOTTOM_RIGHT",
] as const;

export type BatesStampPosition = (typeof BATES_STAMP_POSITIONS)[number];

export const BATES_DEFAULT_STAMP_POSITION: BatesStampPosition = "BOTTOM_RIGHT";

export const isBatesStampPosition = (
  value: unknown
): value is BatesStampPosition =>
  typeof value === "string" &&
  (BATES_STAMP_POSITIONS as readonly string[]).includes(value);

export const BATES_DEFAULT_STAMP_COLOR = "#000000";

export type BatesFormatSettings = {
  batesPrefixSource?: BatesPrefixSource;
  batesPrefix?: string | null;
  batesSuffixSource?: BatesSuffixSource;
  batesSuffix?: string | null;
  batesStartNumber?: number;
  batesDigits?: number;
  batesStampColor?: string;
  batesStampPosition?: BatesStampPosition;
};

export type OrganizationSettingsWithBates = GetOrganizationSettings200 &
  BatesFormatSettings & {
    batesNumberingEnabled?: boolean;
  };

export type UpdateOrganizationSettingsWithBates =
  UpdateOrganizationSettingsBody &
    BatesFormatSettings & {
      batesNumberingEnabled?: boolean;
    };

export const formatBatesPreview = ({
  prefix,
  suffix,
  startNumber,
  digits,
}: {
  prefix: string;
  suffix: string;
  startNumber: number;
  digits: number;
}): string => {
  const clampedDigits = Math.min(
    BATES_MAX_DIGITS,
    Math.max(BATES_MIN_DIGITS, digits)
  );
  const numericPart = String(startNumber).padStart(clampedDigits, "0");
  return [prefix.trim(), numericPart, suffix.trim()]
    .filter(Boolean)
    .join(BATES_SEPARATOR);
};

/** Localized stand-in tokens shown when the real prefix is derived per-document. */
export type BatesPreviewPlaceholders = {
  caseName: string;
  fileName: string;
};

/**
 * Resolves the prefix/suffix and rendered example for a Bates configuration,
 * mirroring the backend's prefix-source precedence: an explicit case-level
 * prefix always wins, otherwise the value is derived from the organization's
 * prefix/suffix source. Shared by the org settings screen, the per-case
 * mini-settings, and the Apply Bates confirmation so previews stay consistent.
 */
export const resolveBatesPreview = ({
  prefixSource,
  customPrefix,
  suffixSource,
  customSuffix,
  explicitPrefix,
  startNumber,
  digits,
  placeholders,
}: {
  prefixSource: BatesPrefixSource;
  customPrefix: string;
  suffixSource: BatesSuffixSource;
  customSuffix: string;
  explicitPrefix?: string;
  startNumber: number;
  digits: number;
  placeholders: BatesPreviewPlaceholders;
}): { prefix: string; suffix: string; text: string } => {
  const trimmedExplicit = explicitPrefix?.trim() ?? "";
  const prefix = trimmedExplicit
    ? trimmedExplicit
    : prefixSource === "CUSTOM"
      ? customPrefix
      : prefixSource === "FILE_NAME"
        ? placeholders.fileName
        : placeholders.caseName;
  const suffix =
    suffixSource === "CUSTOM"
      ? customSuffix
      : suffixSource === "FILE_NAME"
        ? placeholders.fileName
        : "";
  return {
    prefix,
    suffix,
    text: formatBatesPreview({ prefix, suffix, startNumber, digits }),
  };
};

export type CreateProjectBodyWithBates = CreateProjectBody & {
  batesPrefix?: string;
  batesNumberingDisabled?: boolean;
};

/** Case folder with the bates fields stored on the Folder model. */
export type FolderWithBates = Folder & {
  batesNumberingDisabled?: boolean;
  batesPrefix?: string | null;
};

/** Bates stamping lifecycle states (mirrors Prisma enum) */
export type BatesStampingStatus =
  | "NONE"
  | "STAMPING"
  | "STAMPED"
  | "DETECTED_EXISTING"
  | "SKIPPED";

/**
 * Document with the bates fields the API returns.
 *
 * The bates fields are omitted from the generated `Document` before being
 * re-declared so this hand-written union is authoritative: while the deployed
 * OpenAPI spec lags behind (e.g. before the STAMPING enum value ships), the
 * generated `Document.batesStampingStatus` would otherwise intersect with and
 * narrow away states the UI already needs to render.
 */
export type DocumentWithBates = Omit<
  Document,
  "batesStampingStatus" | "detectedBatesPrefix"
> & {
  batesStampingStatus?: BatesStampingStatus;
  detectedBatesPrefix?: string | null;
  firstBatesNumber?: string | null;
};

/** Response from POST .../documents/apply-bates */
export interface ApplyBatesResponse {
  prefix: string;
  documents: { documentId: string; firstBatesNumber: string }[];
}

/** Response from POST .../documents/bates-status */
export interface UpdateBatesStatusResponse {
  updated: number;
  status: BatesStampingStatus;
}
